import {
  createApi,
  requestDataToSnakeCase,
  requestQueryParamsToSnakeCase,
  responseDataToCamelCase,
  responseErrorDataToCamelCase,
  type AxiosError,
  type AxiosInstance,
  type BaseAuthServiceInterface,
  type CreateAxiosDefaults,
  type InternalAxiosRequestConfig,
} from '@mint-lib/api';
import type { Client } from '@mint-lib/api-zod-support';
import * as sdks from '@mint-sdk/api-core';

import { HttpStatus } from '../../../api/src/HttpStatus.js';
import type { MintAdminServiceLocatorAbstractFactoryContext } from '../types.js';

/**
 * Builds an axios instances with the following types:
 * - guestApi: no authentication
 * - accessApi: uses access token for authentication (used only for requesting workspace access tokens)
 * - defaultApi: uses access token for a workspace
 */
export async function httpFactory(
  ctx: MintAdminServiceLocatorAbstractFactoryContext,
  type: 'guestApi' | 'defaultApi' | (string & {}),
  config?: CreateAxiosDefaults,
): Promise<AxiosInstance> {
  const client = createApi({
    baseURL: '/',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    paramsSerializer: { indexes: null },
  });
  client.interceptors.request.use(
    requestQueryParamsToSnakeCase,
    Promise.reject,
  );
  client.interceptors.request.use(requestDataToSnakeCase, Promise.reject);
  if (type === 'guestApi') {
    client.interceptors.response.use(
      responseDataToCamelCase,
      responseErrorDataToCamelCase,
    );
    sdks.guestApi.provideClient(client as unknown as Client);
  }
  if (['defaultApi'].includes(type)) {
    const auth = await ctx.getOrThrow('auth');

    client.interceptors.response.use(
      responseDataToCamelCase,
      buildErrorInterceptor(auth),
    );
    client.interceptors.request.use(
      buildAccessTokenMiddleware(auth),
      Promise.reject,
    );

    sdks.defaultApi.provideClient(client as unknown as Client);

    ctx.on('auth', 'auth', ctx.invalidate);
    ctx.on('auth', 'logout', () => ctx.invalidate());
  }
  return client;
}

function buildErrorInterceptor(auth: BaseAuthServiceInterface) {
  return function (err: AxiosError) {
    if (err.response?.status === HttpStatus.Unauthorized) {
      auth?.logout(err);
    }
    return responseErrorDataToCamelCase(err);
  };
}

function buildAccessTokenMiddleware(auth: BaseAuthServiceInterface) {
  return async (config: InternalAxiosRequestConfig) => {
    if (import.meta.env.VITEST_WORKER_ID || auth === null) {
      return config;
    }
    return {
      ...config,
      headers: {
        ...config.headers,
        Authorization: await auth.provideBearerToken(),
      },
    } as InternalAxiosRequestConfig;
  };
}
